"use strict";
/**
 *
 * Dialog
 *
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importDefault(require("react"));
const clsx_1 = __importDefault(require("clsx"));
const styles_1 = require("../styles");
const utils_1 = require("../utils");
const Modal_1 = require("../Modal");
const Backdrop_1 = require("../Backdrop");
const Fade_1 = require("../Fade");
const transition_1 = require("../styles/transition");
const Paper_1 = require("../Paper");
const styles = styles_1.createStyles({
    /* Styles applied to the root element. */
    root: {
        '@media print': {
            // Use !important to override the Modal inline-style.
            position: 'absolute !important',
        },
    },
    /* Styles applied to the container element if `scroll="paper"`. */
    scrollPaper: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    /* Styles applied to the container element if `scroll="body"`. */
    scrollBody: {
        overflowY: 'auto',
        overflowX: 'hidden',
        textAlign: 'center',
        '&:after': {
            content: '""',
            display: 'inline-block',
            verticalAlign: 'middle',
            height: '100%',
            width: '0',
        },
    },
    /* Styles applied to the container element. */
    container: {
        height: '100%',
        '@media print': {
            height: 'auto',
        },
        // We disable the focus ring for mouse, touch and keyboard users.
        outline: 0,
    },
    /* Styles applied to the `Paper` component. */
    paper: {
        margin: 48,
        position: 'relative',
        overflowY: 'auto',
        '@media print': {
            overflowY: 'visible',
            boxShadow: 'none',
        },
    },
    /* Styles applied to the `Paper` component if `scroll="paper"`. */
    paperScrollPaper: {
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100% - 96px)',
    },
    /* Styles applied to the `Paper` component if `scroll="body"`. */
    paperScrollBody: {
        display: 'inline-block',
        verticalAlign: 'middle',
        textAlign: 'left',
    },
    /* Styles applied to the `Paper` component if `maxWidth=false`. */
    paperWidthFalse: {
        maxWidth: 'calc(100% - 96px)',
    },
    /* Styles applied to the `Paper` component if `fullWidth={true}`. */
    paperFullWidth: {
        width: 'calc(100% - 96px)',
    },
    /* Styles applied to the `Paper` component if `fullScreen={true}`. */
    paperFullScreen: {
        margin: 0,
        width: '100%',
        maxWidth: '100%',
        height: '100%',
        maxHeight: 'none',
        borderRadius: 0,
        '&$paperScrollBody': {
            margin: 0,
            maxWidth: '100%',
        },
    },
});
const defaultTransitionDuration = {
    enter: transition_1.defaultDuration.enteringScreen,
    exit: transition_1.defaultDuration.leavingScreen,
};
/**
 * Dialogs are overlaid modal paper based components with a backdrop.
 */
const Dialog = react_1.default.forwardRef(function Dialog(props, ref) {
    const { BackdropProps, children, classes, className, disableBackdropClick = false, disableEscapeKeyDown = false, fullScreen = false, fullWidth = false, maxWidth = 'sm', onBackdropClick, onClose, onEnter, onEntered, onEntering, onEscapeKeyDown, onExit, onExited, onExiting, open, PaperComponent = Paper_1.Paper, PaperProps = {}, scroll = 'paper', TransitionComponent = Fade_1.Fade, transitionDuration = defaultTransitionDuration, TransitionProps } = props, other = __rest(props, ["BackdropProps", "children", "classes", "className", "disableBackdropClick", "disableEscapeKeyDown", "fullScreen", "fullWidth", "maxWidth", "onBackdropClick", "onClose", "onEnter", "onEntered", "onEntering", "onEscapeKeyDown", "onExit", "onExited", "onExiting", "open", "PaperComponent", "PaperProps", "scroll", "TransitionComponent", "transitionDuration", "TransitionProps"]);
    const mouseDownTarget = react_1.default.useRef();
    const handleMouseDown = (event) => {
        mouseDownTarget.current = event.target;
    };
    const handleBackdropClick = (event) => {
        // Ignore the events not coming from the "backdrop"
        // We don't want to close the dialog when clicking the dialog content.
        if (event.target !== event.currentTarget) {
            return;
        }
        // Make sure the event starts and ends on the same DOM element.
        if (event.target !== mouseDownTarget.current) {
            return;
        }
        mouseDownTarget.current = null;
        if (onBackdropClick) {
            onBackdropClick(event);
        }
        if (!disableBackdropClick && onClose) {
            onClose(event, 'backdropClick');
        }
    };
    return (react_1.default.createElement(Modal_1.Modal, Object.assign({ className: clsx_1.default(classes.root, className), BackdropComponent: Backdrop_1.Backdrop, BackdropProps: Object.assign({ transitionDuration }, BackdropProps), closeAfterTransition: true, disableBackdropClick: disableBackdropClick, disableEscapeKeyDown: disableEscapeKeyDown, onEscapeKeyDown: onEscapeKeyDown, onClose: onClose, open: open, ref: ref }, other),
        react_1.default.createElement(TransitionComponent, Object.assign({ appear: true, in: open, timeout: transitionDuration, onEnter: onEnter, onEntering: onEntering, onEntered: onEntered, onExit: onExit, onExiting: onExiting, onExited: onExited, role: "none presentation" }, TransitionProps),
            react_1.default.createElement("div", { className: clsx_1.default(classes.container, classes[`scroll${utils_1.capitalize(scroll)}`]), onClick: handleBackdropClick, onMouseDown: handleMouseDown },
                react_1.default.createElement(PaperComponent, Object.assign({ elevation: 24, role: "dialog" }, PaperProps, { className: clsx_1.default(classes.paper, classes[`paperScroll${utils_1.capitalize(scroll)}`], classes[`paperWidth${utils_1.capitalize(String(maxWidth))}`], {
                        [classes.paperFullScreen]: fullScreen,
                        [classes.paperFullWidth]: fullWidth,
                    }, PaperProps.className) }), children)))));
});
if (process.env.NODE_ENV !== 'production') {
    Dialog.displayName = 'Dialog';
}
const StyledDialog = styles_1.withStyles(styles)(Dialog);
exports.Dialog = StyledDialog;
